/**
 * AgendaView — 어젠다(리스트) 뷰
 *
 * 종일 카드는 옅은 primary 배경 + "종일" 라벨.
 * 시간 지정 카드는 기존 border + 시간 범위.
 */
import React, { useMemo } from 'react';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { CalendarEvent, EventChipStyle } from '../../../models/CalendarEvent';
import { cn } from '../../../lib/utils';
import { CalendarDays, MapPin } from 'lucide-react';
import ChipMarkerIcon from './ChipMarkerIcon';
import { useCalendarLabels } from '../useCalendarLabels';

interface Props {
  anchorDate: Date;
  events: CalendarEvent[];
  loading: boolean;
  onEventClick?: (event: CalendarEvent, e?: React.MouseEvent) => void;
  getStyle: (event: CalendarEvent) => EventChipStyle;
}

interface DayGroup {
  date: Date;
  events: CalendarEvent[];
}

function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

function groupByDay(events: CalendarEvent[]): DayGroup[] {
  const map = new Map<string, DayGroup>();
  for (const e of events) {
    const d = startOfDay(new Date(e.start_at));
    const key = d.toISOString();
    if (!map.has(key)) {
      map.set(key, { date: d, events: [] });
    }
    map.get(key)!.events.push(e);
  }
  return Array.from(map.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
}

function formatDateLabel(d: Date): string {
  const today = startOfDay(new Date());
  const target = startOfDay(d);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  // locale 날짜 + 요일 (예: ko "6월 5일 (금)", ja "6月5日(金)", en "Fri, Jun 5")
  const dateStr = d.toLocaleDateString(i18n.locale || 'en', { month: 'long', day: 'numeric', weekday: 'short' });
  if (diff === 0) return `${t`Today`} · ${dateStr}`;
  if (diff === 1) return `${t`Tomorrow`} · ${dateStr}`;
  if (diff === -1) return `${t`Yesterday`} · ${dateStr}`;
  return dateStr;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(i18n.locale || 'en', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatTimeRange(event: CalendarEvent): string {
  if (event.is_all_day) return t`All day`;
  return `${formatTime(event.start_at)} – ${formatTime(event.end_at)}`;
}

const AgendaView: React.FC<Props> = ({ anchorDate, events, loading, onEventClick, getStyle }) => {
  const labels = useCalendarLabels();
  const groups = useMemo(() => {
    const anchorStart = startOfDay(anchorDate);
    const future = events.filter((e) => new Date(e.start_at) >= anchorStart);
    return groupByDay(future);
  }, [events, anchorDate]);

  if (loading && groups.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-caption text-muted-foreground">
        <Trans>Loading events…</Trans>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
        <div className="size-12 rounded-2xl bg-primary/10 flex items-center justify-center">
          <CalendarDays className="size-6 text-primary" strokeWidth={1.5} />
        </div>
        <p className="text-body font-medium text-foreground"><Trans>No events scheduled</Trans></p>
        <p className="text-caption"><Trans>Add events using the [New Event] button at top right</Trans></p>
      </div>
    );
  }

  return (
    <div className="max-w-820 mx-auto px-4 py-4 h-full overflow-y-auto">
      <div className="flex flex-col gap-6">
        {groups.map((group) => (
          <section key={group.date.toISOString()} className="flex flex-col gap-2">
            <h2 className="text-label font-semibold text-muted-foreground sticky top-0 bg-background py-1">
              {formatDateLabel(group.date)}
            </h2>
            <ul className="flex flex-col gap-1.5">
              {group.events.map((event) => {
                const style = getStyle(event);
                const categoryLabel = labels.category(style.category);
                return (
                  <li
                    key={event.id}
                    onClick={(e) => onEventClick?.(event, e)}
                    className={cn(
                      'flex items-start gap-3 p-3 rounded-card border transition-colors cursor-pointer',
                      event.is_all_day
                        ? cn('border-transparent hover:opacity-90', style.softBg)
                        : 'border-border bg-card hover:bg-muted/40',
                      style.borderExtra && `border ${style.borderExtra}`,
                    )}
                  >
                    <div className={cn(
                      'shrink-0 w-20 text-caption tabular-nums pt-0.5',
                      event.is_all_day
                        ? 'font-semibold text-foreground'
                        : 'text-muted-foreground',
                    )}>
                      {formatTimeRange(event)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <ChipMarkerIcon marker={style.marker} className={cn('size-3.5', style.markerColor)} />
                        <span className="text-body font-medium text-foreground truncate">
                          {event.title}
                        </span>
                        <span className="text-meta text-muted-foreground shrink-0">
                          {categoryLabel}
                        </span>
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-1 text-caption text-muted-foreground mt-0.5">
                          <MapPin className="size-3" />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
};

export default AgendaView;
